import { customElement, property, query } from 'lit/decorators.js';
import { WEB_COMPONENT_NETWORK_UPDATE_AGENT } from '../../../../../webcomponents.constants';
import { html, LitElement, TemplateResult } from 'lit';
import { styles } from './view/styles';
import { translateFn } from '../../../../../utils/translator.helper';
import { FontSizes } from '../../../../../types/common/types';
import { ComponentsSelectionOptions } from './types';
import { InfoBoxSeverity } from 'apps/webcomponents/src/ui/gzInfoBox/types';
import { WebComponentEvent } from '../../../../../framework/BaseWebComponentController';
import { GzCheckboxWebComponent } from '../../../../../ui/gzCheckbox';
import { GzInfoBoxWebComponent } from '../../../../../ui/gzInfoBox';
import { GzRadioGroupWebComponent } from '../../../../../ui/gzRadio';
import { GzFormFieldWebComponent } from '../../../../../ui/gzForm/formField';
import { UpdateAgentTaskSpecifics } from '../types';
import { NetworkPopUpEvents } from '../../NetworkBasePopUp';

@customElement(WEB_COMPONENT_NETWORK_UPDATE_AGENT)
export class GzUpdateAgentWebComponent extends LitElement {
  @property({ type: Boolean, attribute: true, reflect: true })
  public shouldDisableProductCheckbox = false;

  static get styles() {
    return styles;
  }

  @query('#components-selection-radio')
  private componentsSelectionRadio: GzRadioGroupWebComponent;

  @query('#checkboxes-container')
  private checkboxesContainer: HTMLDivElement;

  @query('#security-content-form-field')
  private securityContentFormField: GzFormFieldWebComponent<GzCheckboxWebComponent>;

  @query('#product-form-field')
  private productFormField: GzFormFieldWebComponent<GzCheckboxWebComponent>;

  @query('#selection-warning')
  private selectionWarning: GzInfoBoxWebComponent;

  private handleComponentsCheckboxChange(event: WebComponentEvent) {
    event.stopImmediatePropagation();
    const isAnySelected = this.securityContentFormField.Input.checked || this.productFormField.Input.checked;

    if (isAnySelected) {
      this.selectionWarning.classList.add('invisible');
      this.toggleConfirmationButton(false);
    } else {
      this.selectionWarning.classList.remove('invisible');
      this.toggleConfirmationButton(true);
    }
  }

  public getRequestTaskSpecifics(): UpdateAgentTaskSpecifics {
    return {
      credentials: [],
      uiSettings: {
        updateProduct: !this.productFormField.Input.disabled && this.productFormField.Input.checked,
        updateSignatures: !this.securityContentFormField.Input.disabled && this.securityContentFormField.Input.checked,
        usePolicySettings:
          this.componentsSelectionRadio.selectedOption === ComponentsSelectionOptions.CHOOSE_COMPONENTS,
      },
    };
  }

  private handleComponentsSelectionOptionChanged(event: CustomEvent) {
    event.stopImmediatePropagation();
    if (event.detail.selectedValue === ComponentsSelectionOptions.USE_POLICY_DEFINED_COMPONENTS) {
      this.toggleConfirmationButton(false);
      this.selectionWarning.classList.add('invisible');
      this.checkboxesContainer.classList.add('disabled');
      this.securityContentFormField.Input.setEnabled(false);
      this.productFormField.Input.setEnabled(false);
    } else {
      if (!this.productFormField.Input.checked && !this.securityContentFormField.Input.checked) {
        this.selectionWarning.classList.remove('invisible');
        this.toggleConfirmationButton(true);
      }

      this.checkboxesContainer.classList.remove('disabled');
      this.productFormField.Input.setEnabled(!this.shouldDisableProductCheckbox);
      this.securityContentFormField.Input.setEnabled(true);
    }
  }

  private toggleConfirmationButton(disabled: boolean) {
    this.dispatchEvent(
      new CustomEvent(NetworkPopUpEvents.SET_CONFIRM_BUTTON, {
        bubbles: true,
        composed: true,
        detail: { value: disabled },
      })
    );
  }

  render(): TemplateResult {
    return html`
      <div id="wrapper" class="wrapper">
        <gz-text class="mb-2" fontSize="${FontSizes.HEADING_6}">
          ${translateFn('The security agent on the target endpoints will update based on the selected options.')}
        </gz-text>
        <gz-radio-group
          id="components-selection-radio"
          gz-form-input
          groupName="update-components-selection"
          .selectedOption="${ComponentsSelectionOptions.CHOOSE_COMPONENTS}"
          @radioSelectionChanged="${this.handleComponentsSelectionOptionChanged}"
        >
          <gz-radio
            name="choose-components"
            class="mb-2"
            .value="${ComponentsSelectionOptions.CHOOSE_COMPONENTS}"
            label="${translateFn('Choose components')}"
          ></gz-radio>
          <gz-text class="mb-2 helper-text" fontSize="${FontSizes.PARAGRAPH_12}">
            ${translateFn('The security agent will update based on your current preferences.')}
          </gz-text>

          <gz-info-box id="selection-warning" severity="${InfoBoxSeverity.WARNING}" class="invisible">
            ${translateFn('Select at least one option.')}
          </gz-info-box>
          <div id="checkboxes-container" class="ml-3 mb-3">
            <gz-form-field id="security-content-form-field">
              <gz-checkbox
                gz-form-input
                id="security-content-checkbox"
                label="${translateFn('Security content')}"
                class="field-container"
                @change="${this.handleComponentsCheckboxChange}"
                ?checked="${true}"
              ></gz-checkbox>
            </gz-form-field>
            <gz-form-field id="product-form-field" ?disabled="${this.shouldDisableProductCheckbox}">
              <gz-checkbox
                gz-form-input
                id="product-checkbox"
                label="${translateFn('Product')}"
                class="field-container"
                @change="${this.handleComponentsCheckboxChange}"
                ?checked="${!this.shouldDisableProductCheckbox}"
              ></gz-checkbox>
            </gz-form-field>
          </div>
          <gz-radio
            name="use-policy-defined-components"
            .value="${ComponentsSelectionOptions.USE_POLICY_DEFINED_COMPONENTS}"
            label="${translateFn('Use policy-defined components')}"
          ></gz-radio>
          <gz-text class="pt-1 helper-text" fontSize="${FontSizes.PARAGRAPH_12}">
            ${translateFn('The security agent will update according to the settings of the active policy.')}
          </gz-text>
        </gz-radio-group>
      </div>
    `;
  }
}
